import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { CurrentAccount } from '../../../types/account';
import { getCurrentAccount } from '../../../store/request';

type CurrentAccountContextValue = {
  account: CurrentAccount | null;
  refreshAccount: () => Promise<CurrentAccount | null>;
};

const CurrentAccountContext = createContext<CurrentAccountContextValue>({
  account: null,
  refreshAccount: async () => null
});

export function CurrentAccountProvider({
  account: initialAccount,
  children
}: {
  account: CurrentAccount | null;
  children: React.ReactNode;
}) {
  const [account, setAccount] = useState<CurrentAccount | null>(initialAccount);

  React.useEffect(() => {
    setAccount(initialAccount);
  }, [initialAccount]);

  const refreshAccount = useCallback(async () => {
    try {
      const res = await getCurrentAccount();
      if (res.code === 200 && res.content) {
        setAccount(res.content);
        return res.content;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, []);

  const value = useMemo(() => ({ account, refreshAccount }), [account, refreshAccount]);

  return <CurrentAccountContext.Provider value={value}>{children}</CurrentAccountContext.Provider>;
}

export function useCurrentAccount() {
  return useContext(CurrentAccountContext).account;
}

export function useRefreshCurrentAccount() {
  return useContext(CurrentAccountContext).refreshAccount;
}
